import type { IAPIErrorDetails } from '@manager/api/types/api-error-types';
import type {
  IForm,
  IFormField,
  IFormFieldUnwrapped,
} from '@manager/types/form-types';
import {
  getApiError,
  translateApiFieldError,
} from '@manager/utils/error-utils';
import type { ValidationFnFactory } from '@manager/utils/validation-rules-utils';
import { requiredLabel } from '@manager/utils/validation-rules-utils';
import { isArray, isEqual, isString, set } from 'lodash-es';
import type { Maybe } from 'shared/types';
import { toArray } from 'shared/utils';
import {
  computed,
  type ComputedRef,
  isRef,
  type MaybeRef,
  type MaybeRefOrGetter,
  nextTick,
  type Ref,
  ref,
  toValue,
  unref,
  watch,
} from 'vue';

export type IFormFieldApiName = (string | RegExp)[] | undefined;

function getApiErrorDetails(errorResponse: MaybeRefOrGetter<unknown>) {
  const reported = getApiError(toValue(errorResponse))?.error.details;

  return isArray(reported) ? reported : [];
}

/** A field's `responseName`, falling back to its key in the form. */
function getFieldApiNames<T extends IForm<R>, R>(form: T, field: keyof T) {
  return (unref(form[field]?.responseName) ||
    unref(field)) as IFormFieldApiName;
}

/**
 * Maps an API error's field details onto the matching form fields, clearing the rest. Accepts
 * manager-api's error envelope and billing-api's problem document alike — `getApiError`
 * normalizes both, so a field only needs its `responseName` to match the reported path.
 */
export function useFormErrors<T extends IForm<R>, R>(
  form: T,
  errorResponse: MaybeRefOrGetter<unknown>
): T {
  const details = computed(() => getApiErrorDetails(errorResponse));

  watch(
    () => toValue(errorResponse),
    () => {
      const fields = Object.keys(form) as (keyof T)[];

      fields.forEach((field) => {
        const names = getFieldApiNames(form, field);

        const fieldErrors = names
          ? details.value.filter((d) => matchesName(names, d.field))
          : [];

        if (fieldErrors.length > 0) {
          form[field].setError(fieldErrors);
        } else {
          form[field].clearError();
        }
      });
    }
    // This breaks the connected app forms :(
    // { immediate: true }
  );

  return form;
}

/**
 * Messages for the details `useFormErrors` could attach to no field, translated with the reported
 * path in place of a label. Render them where the form cannot — a form-level alert.
 */
export function useUnmatchedFormErrorMessages<T extends IForm<R>, R>(
  form: T,
  errorResponse: MaybeRefOrGetter<unknown>
): ComputedRef<string[]> {
  return computed(() => {
    const fields = Object.keys(form) as (keyof T)[];

    return getApiErrorDetails(errorResponse)
      .filter(
        (detail) =>
          !fields.some((field) =>
            matchesName(getFieldApiNames(form, field), detail.field)
          )
      )
      .map((detail) => translateApiFieldError(detail, detail.field));
  });
}

export type IFormFieldErrorMapping = string | RegExp | IFormFieldApiName;

export function useFormField<
  T,
  TProps extends object = Record<string, unknown>,
>(
  givenLabel: MaybeRef<string>,
  initialValue: T,
  givenRules?: MaybeRef<ValidationFnFactory[]>,
  givenResponseName?: MaybeRef<IFormFieldErrorMapping>
): IFormField<T, TProps> {
  const value = ref(initialValue) as Ref<T>;
  const label = ref(givenLabel);
  const responseName = ref(givenResponseName);
  const rules = ref(givenRules);
  const errors = ref<IAPIErrorDetails[]>([]);
  const additionalProps = ref<undefined | TProps>(undefined);

  const required = computed(() =>
    (rules.value || []).some((r) => !!r?.prototype?.required)
  );

  const rulesArray = computed(() => {
    return (rules.value || []).map((r) => r(label.value));
  });

  const computedLabel = computed(() =>
    required.value ? requiredLabel(label.value) : label.value
  );

  const finalResponseName: ComputedRef<IFormFieldApiName> = computed(() =>
    responseName.value != null ? toArray(responseName.value) : undefined
  );

  function clearError() {
    errors.value = [];
  }

  watch(value, () => clearError(), { deep: true });

  return {
    value,
    bindings: computed(() => {
      let additionalPropsToSet: {
        error?: boolean;
        errorMessages?: string[];
      } = {};

      if (errors.value.length > 0) {
        additionalPropsToSet['error'] = true;
        additionalPropsToSet['errorMessages'] = errors.value.map((detail) =>
          translateApiFieldError(detail, label.value)
        );
      }

      if (additionalProps.value) {
        additionalPropsToSet = {
          ...additionalPropsToSet,
          ...additionalProps.value,
        };
      }

      return {
        label: computedLabel.value,
        rawLabel: label.value,
        rules: rulesArray.value,
        ...additionalPropsToSet,
      };
    }),
    setError(apiErrors: IAPIErrorDetails[]) {
      errors.value = apiErrors;
    },
    additionalProps: additionalProps as Ref<TProps | undefined>,
    clearError,
    responseName: finalResponseName,
  };
}

function matchesName(haystack: IFormFieldApiName, needle: string) {
  // A per-item failure (`locationIds.0`) belongs to the control holding the whole value.
  const candidates = [needle, needle.replace(/\.\d+$/, '')];

  return toArray(haystack)?.some((s) => {
    if (!s) {
      return false;
    }

    if (isString(s)) {
      return candidates.includes(s);
    } else {
      return candidates.some((candidate) => s.test(candidate));
    }
  });
}

export type IMaybeReactiveForm<TFormModel> = {
  [K in keyof TFormModel]:
    | IFormField<TFormModel[K]>
    | IFormFieldUnwrapped<TFormModel[K], unknown>;
};

/**
 * Extracts current values from a form object, unwrapping refs if necessary.
 * Constructs a plain object with the same shape and types as the TFormModel.
 * @param form
 */
export function getFormValues<TFormModel>(
  form: IMaybeReactiveForm<TFormModel>
): TFormModel {
  const values = {} as TFormModel;

  for (const key in form) {
    const field = form[key];
    values[key] = unref(field.value);
  }

  return values;
}

export function assignFormValues<TFormModel extends object>(
  form: IMaybeReactiveForm<TFormModel>,
  values: Partial<TFormModel>
) {
  const keys = Object.keys(values) as (keyof TFormModel)[];

  for (const prop of keys) {
    if (isRef(form[prop].value)) {
      (form[prop].value as Ref).value = values[prop];
    } else {
      // you will always end up here because of ref unwrapping
      form[prop].value = values[prop] as TFormModel[keyof TFormModel];
    }
  }
}

export type IFormChangeType = 'added' | 'removed' | 'changed' | 'same';
export type IFormChange<T> = {
  change: IFormChangeType;
  changed: boolean;
  initialValue: T;
  currentValue: T;
};

export type IGetFormChangesReturnType<TFormModel> = {
  [key in keyof TFormModel]: IFormChange<TFormModel[key]>;
};

export function getFormChanges<TFormModel extends object>(
  initial: TFormModel,
  compareTo: TFormModel
): IGetFormChangesReturnType<TFormModel> {
  const keys = Object.keys(initial) as (keyof TFormModel)[];

  const changes: Partial<IGetFormChangesReturnType<TFormModel>> = {};

  keys.forEach((key) => {
    const initialValue = initial[key];
    const currentValue = compareTo[key];
    let change: IFormChangeType = 'same';

    if (initialValue == null && currentValue != null) {
      change = 'added';
    } else if (initialValue != null && currentValue == null) {
      change = 'removed';
    } else if (!isEqual(initialValue, currentValue)) {
      change = 'changed';
    }

    set(changes, key, {
      change,
      changed: change !== 'same',
      initialValue,
      currentValue,
    });
  });

  return changes as IGetFormChangesReturnType<TFormModel>;
}

export interface IGetFieldChangesItem<
  TFormModel extends object,
  TKey = keyof TFormModel,
> {
  field: TKey;
  change: IFormChange<TKey>;
}

export function getFormFieldChanges<TFormModel extends object>(
  bookingFormValues: Maybe<TFormModel>,
  formValues: Maybe<TFormModel>,
  fieldsToCheck: (keyof TFormModel)[]
): IGetFieldChangesItem<TFormModel>[] {
  if (!bookingFormValues || !formValues) {
    return [];
  }

  const changesToOriginalBooking = getFormChanges(
    bookingFormValues,
    formValues
  );

  if (changesToOriginalBooking) {
    const keys = (
      Object.keys(changesToOriginalBooking) as (keyof TFormModel)[]
    ).filter((key) => fieldsToCheck.includes(key));

    return keys
      .map((key) => {
        const change = changesToOriginalBooking?.[key];

        if (change?.changed) {
          return {
            field: key,
            change: changesToOriginalBooking?.[key],
          };
        }
      })
      .filter((m) => !!m) as IGetFieldChangesItem<TFormModel>[];
  }

  return [];
}

/**
 * Scrolls the first invalid form control into view and applies a subtle shake animation.
 * This is intended to be called after a failed form validation.
 */
export async function scrollToFirstInvalidFormControl(
  formElement?: HTMLElement | null
) {
  if (!formElement) {
    return;
  }

  // Wait for the DOM to reflect the latest validation state
  await nextTick();

  const selectors = [
    // Typical Vuetify error indicators
    '.v-input--error',
    '.v-field--error',
    // Generic invalid state
    '[aria-invalid="true"]',
  ];

  let target: HTMLElement | null = formElement.querySelector<HTMLElement>(
    selectors.join(', ')
  );

  // Fallback: scroll to the first visible error message inside the form
  if (!target) {
    const message = formElement.querySelector<HTMLElement>(
      '.v-messages__message'
    );
    if (message) {
      target = message;
    }
  }

  if (!target) {
    return;
  }

  // Ensure the relevant control (or its container) is visible
  target.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });

  // Apply a subtle shake animation class.
  const elementForAnimation = target.closest(
    '.v-input, .v-field'
  ) as HTMLElement | null;

  const animated = elementForAnimation || target;

  // Skip if animation is already in progress to prevent layout glitches
  // when spamming the submit button
  if (animated.classList.contains('form-field-shake')) {
    return;
  }

  animated.classList.add('form-field-shake');

  // Remove the class after animation completes to allow future animations
  animated.addEventListener(
    'animationend',
    () => {
      animated.classList.remove('form-field-shake');
    },
    { once: true }
  );
}
